import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PublicLayout } from '~/components/layout/PublicLayout.js';
import { Loading } from '~/components/ui/Feedback.js';
import { ApiRequestError } from '~/lib/api.js';
import { ThemeProvider } from '~/lib/theme.js';
import { HomePage } from '~/pages/public/HomePage.js';
import { JobDetailPage } from '~/pages/public/JobDetailPage.js';
import { NotFoundPage } from '~/pages/public/NotFoundPage.js';
import { PrivacyPage } from '~/pages/public/PrivacyPage.js';

/**
 * The admin is a separate bundle. Nobody arriving to read a job description
 * should have to download the applicant-tracking screens to see it.
 */
const AdminLayout = lazy(() =>
  import('~/components/admin/AdminLayout.js').then((module) => ({ default: module.AdminLayout })),
);
const DashboardPage = lazy(() =>
  import('~/pages/admin/DashboardPage.js').then((module) => ({ default: module.DashboardPage })),
);
const JobsListPage = lazy(() =>
  import('~/pages/admin/JobsListPage.js').then((module) => ({ default: module.JobsListPage })),
);
const JobEditorPage = lazy(() =>
  import('~/pages/admin/JobEditorPage.js').then((module) => ({ default: module.JobEditorPage })),
);
const ApplicationsListPage = lazy(() =>
  import('~/pages/admin/ApplicationsListPage.js').then((module) => ({
    default: module.ApplicationsListPage,
  })),
);
const CandidatesPage = lazy(() =>
  import('~/pages/admin/CandidatesPage.js').then((module) => ({ default: module.CandidatesPage })),
);
const ApplicationDetailPage = lazy(() =>
  import('~/pages/admin/ApplicationDetailPage.js').then((module) => ({
    default: module.ApplicationDetailPage,
  })),
);
const ContactMessagesListPage = lazy(() =>
  import('~/pages/admin/ContactMessagesListPage.js').then((module) => ({
    default: module.ContactMessagesListPage,
  })),
);
const ContactMessageDetailPage = lazy(() =>
  import('~/pages/admin/ContactMessageDetailPage.js').then((module) => ({
    default: module.ContactMessageDetailPage,
  })),
);

/** The application form pulls in form and validation code the other pages do not need. */
const ApplyPage = lazy(() =>
  import('~/pages/public/ApplyPage.js').then((module) => ({ default: module.ApplyPage })),
);

function PageFallback() {
  return (
    <div className="mx-auto max-w-2xl px-5 py-24 sm:px-8">
      <Loading />
    </div>
  );
}

function AdminFallback() {
  return (
    <div className="mx-auto max-w-md px-5 py-24">
      <Loading label="Loading admin" />
    </div>
  );
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // A 4xx will not become a 2xx on retry; only server and network faults are worth repeating.
        retry: (failureCount, error) =>
          error instanceof ApiRequestError && error.status >= 400 && error.status < 500
            ? false
            : failureCount < 2,
      },
    },
  });
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="jobs/:slug" element={<JobDetailPage />} />
        <Route
          path="jobs/:slug/apply"
          element={
            <Suspense fallback={<PageFallback />}>
              <ApplyPage />
            </Suspense>
          }
        />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route
        path="admin"
        element={
          <Suspense fallback={<AdminFallback />}>
            <AdminLayout />
          </Suspense>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<AdminFallback />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="jobs"
          element={
            <Suspense fallback={<AdminFallback />}>
              <JobsListPage />
            </Suspense>
          }
        />
        <Route
          path="jobs/new"
          element={
            <Suspense fallback={<AdminFallback />}>
              <JobEditorPage />
            </Suspense>
          }
        />
        <Route
          path="jobs/:id"
          element={
            <Suspense fallback={<AdminFallback />}>
              <JobEditorPage />
            </Suspense>
          }
        />
        <Route
          path="applications"
          element={
            <Suspense fallback={<AdminFallback />}>
              <ApplicationsListPage />
            </Suspense>
          }
        />
        <Route
          path="applications/:id"
          element={
            <Suspense fallback={<AdminFallback />}>
              <ApplicationDetailPage />
            </Suspense>
          }
        />
        <Route
          path="candidates"
          element={
            <Suspense fallback={<AdminFallback />}>
              <CandidatesPage />
            </Suspense>
          }
        />
        <Route
          path="contact-messages"
          element={
            <Suspense fallback={<AdminFallback />}>
              <ContactMessagesListPage />
            </Suspense>
          }
        />
        <Route
          path="contact-messages/:id"
          element={
            <Suspense fallback={<AdminFallback />}>
              <ContactMessageDetailPage />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}

export function App({ queryClient = createQueryClient() }: { queryClient?: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
